/**
 * Consent regimes drive how aggressive our analytics defaults are:
 *
 * - 'opt-in'  — EU/UK/EEA/CH/Quebec. Nothing tracks until explicit consent.
 *               GDPR / UK PECR / Quebec Law 25 require prior opt-in.
 * - 'ccpa'    — California. Tracking starts on landing; we MUST offer a
 *               "Do Not Sell or Share My Personal Information" mechanism.
 * - 'opt-out' — Rest of world (US, Canada-non-Quebec, AU, LATAM, Asia, etc).
 *               Tracking starts on landing; small notice with decline link.
 *
 * Resolved in MIDDLEWARE (edge) from Vercel's geo headers into the readable
 * `evdev_regime` cookie — never in a Server Component: a cookies()/headers()
 * read in the root layout would opt every route into dynamic rendering and
 * kill CDN caching. Client components read the cookie at runtime. In dev (or
 * behind a non-Vercel proxy) the geo headers are missing — we default to
 * 'opt-in' (safest).
 */
export type ConsentRegime = 'opt-in' | 'ccpa' | 'opt-out';

const EU_EEA_OPT_IN_COUNTRIES = new Set([
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA non-EU
  'IS', 'LI', 'NO',
  // UK
  'GB',
  // Switzerland (revFADP closely mirrors GDPR for tracking)
  'CH',
]);

const QUEBEC_REGION_CODES = new Set(['QC']);
const CALIFORNIA_REGION_CODES = new Set(['CA']);

const VALID_REGIMES: ReadonlySet<ConsentRegime> = new Set([
  'opt-in',
  'opt-out',
  'ccpa',
]);

/** Non-httpOnly cookie set by middleware; read by client consent bootstrap. */
export const REGIME_COOKIE = 'evdev_regime';

/**
 * Pure regime resolution — callable from middleware (edge runtime) and tests.
 * Resolution order: override cookie → Vercel geo headers → 'opt-in' fallback.
 */
export function resolveConsentRegime(input: {
  override?: string;
  country?: string;
  region?: string;
}): ConsentRegime {
  if (input.override && VALID_REGIMES.has(input.override as ConsentRegime)) {
    return input.override as ConsentRegime;
  }
  const country = (input.country ?? '').toUpperCase();
  const region = (input.region ?? '').toUpperCase();

  // No geo header (dev, self-hosted, missing edge) → safest default
  if (!country) return 'opt-in';

  if (EU_EEA_OPT_IN_COUNTRIES.has(country)) return 'opt-in';

  // Quebec Law 25 — opt-in even though Canada otherwise allows opt-out
  if (country === 'CA' && QUEBEC_REGION_CODES.has(region)) return 'opt-in';

  // California CCPA / CPRA — opt-out is allowed but DNSMPI must be offered
  if (country === 'US' && CALIFORNIA_REGION_CODES.has(region)) return 'ccpa';

  return 'opt-out';
}

/** Client-side read of the middleware-set regime cookie ('opt-in' fallback). */
export function readRegimeCookie(): ConsentRegime {
  if (typeof document === 'undefined') return 'opt-in';
  const match = document.cookie.match(/(?:^|;\s*)evdev_regime=([^;]+)/);
  const value = match?.[1];
  return value && VALID_REGIMES.has(value as ConsentRegime)
    ? (value as ConsentRegime)
    : 'opt-in';
}

/** True when the visitor MUST be shown a Do Not Sell / Share link. */
export function regimeRequiresDoNotSell(regime: ConsentRegime): boolean {
  return regime === 'ccpa';
}

/** True when tracking can fire on landing (no prior consent required). */
export function regimeAllowsImpliedConsent(regime: ConsentRegime): boolean {
  return regime === 'opt-out' || regime === 'ccpa';
}
